import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "mdi-material-ui/Close";
import ChevronDownIcon from "mdi-material-ui/ChevronDown";
import RefreshIcon from "mdi-material-ui/Refresh";
import { useSnackbar } from "notistack";
import primaryShopIdQuery from "../../../../graphql/queries/getPrimaryShopId";
import ColumnVisibilityMenu from "../../shared/components/ColumnVisibilityMenu";
import accountsQuery from "../graphql/queries/userAdministrationAccounts";
import groupsQuery from "../graphql/queries/accountGroups";
import setAccountGroupsMutation from "../graphql/mutations/setAccountGroups";

const BOOLEAN_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" }
];

const USER_TYPE_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Business", value: "business" },
  { label: "Family & Friends", value: "family" }
];

const DEFAULT_VISIBLE_COLUMN_IDS = [
  "details",
  "name",
  "email",
  "company",
  "address",
  "city",
  "postal",
  "country",
  "vatId",
  "datevId",
  "groups",
  "backendAccess",
  "confirmedEmail",
  "isActivated",
  "invoicePaymentAllowed",
  "userType",
  "createdAt"
];

const DEFAULT_FILTERS = {};
const GROUPS_PAGE_SIZE = 200;
const BACKEND_ACCESS_PERMISSION = "reaction:legacy:dashboard/read";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  toolbar: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  search: {
    minWidth: 360
  },
  tableWrap: {
    overflowX: "auto"
  },
  backendDot: {
    borderRadius: "50%",
    display: "inline-block",
    height: 10,
    marginRight: theme.spacing(1),
    width: 10
  },
  backendDotActive: {
    background: theme.palette.success ? theme.palette.success.main : "#2e7d32"
  },
  backendDotInactive: {
    background: theme.palette.error ? theme.palette.error.main : "#c62828"
  },
  drawer: {
    padding: theme.spacing(3)
  },
  drawerPaper: {
    width: 860,
    maxWidth: "100vw"
  },
  detailsTable: {
    "& td": {
      verticalAlign: "top"
    }
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5)
  },
  permissionPanel: {
    maxHeight: 360,
    overflowY: "auto"
  },
  permissionGroup: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0)
  },
  permissionSearch: {
    marginBottom: theme.spacing(1)
  },
  groupSummary: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    width: "100%"
  },
  muted: {
    color: theme.palette.text.secondary
  },
  filterCell: {
    backgroundColor: theme.palette.action.hover,
    minWidth: 120,
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    verticalAlign: "top"
  },
  filterInput: {
    minWidth: 120
  },
  actions: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1)
  }
}));

function normalizeText(value) {
  if (value === null || typeof value === "undefined") return "";
  return String(value).trim().toLowerCase();
}

function valueText(value) {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || typeof value === "undefined" || value === "") return "-";
  return String(value);
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("de-DE");
}

function accountGroups(account) {
  if (Array.isArray(account?.groups)) return account.groups;
  if (Array.isArray(account?.groups?.edges)) return account.groups.edges.map((edge) => edge?.node).filter(Boolean);
  return account?.groups?.nodes || [];
}

function normalizeGroupCollection(groups) {
  if (Array.isArray(groups)) return groups;
  if (Array.isArray(groups?.nodes)) return groups.nodes;
  if (Array.isArray(groups?.edges)) return groups.edges.map((edge) => edge?.node).filter(Boolean);
  return [];
}

function groupPermissions(groups) {
  return [...new Set(groups.flatMap((group) => group.permissions || []))].sort();
}

function hasBackendAccess(groups) {
  return groupPermissions(groups).includes(BACKEND_ACCESS_PERMISSION);
}

function hasBackendAccessPermission(group) {
  return Array.isArray(group?.permissions) && group.permissions.includes(BACKEND_ACCESS_PERMISSION);
}

function compareSortValues(left, right) {
  if (left === right) return 0;
  if (left === null || typeof left === "undefined" || left === "") return 1;
  if (right === null || typeof right === "undefined" || right === "") return -1;

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), "de", { numeric: true, sensitivity: "base" });
}

function matchesFilter(column, account, filterValue) {
  if (filterValue === null || typeof filterValue === "undefined" || filterValue === "") {
    return true;
  }

  const rawValue = column.getFilterValue ? column.getFilterValue(account) : column.getValue(account);

  if (column.filterType === "boolean") {
    if (filterValue === "yes") return rawValue === true;
    if (filterValue === "no") return rawValue === false;
    return true;
  }

  if (column.filterType === "select") {
    if (Array.isArray(rawValue)) {
      return rawValue.includes(filterValue);
    }

    return String(rawValue || "") === String(filterValue);
  }

  return normalizeText(rawValue).includes(normalizeText(filterValue));
}

function DetailRow({ label, value }) {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <strong>{label}</strong>
      </TableCell>
      <TableCell>{valueText(value)}</TableCell>
    </TableRow>
  );
}

export default function UserAdministration() {
  const classes = useStyles();
  const apolloClient = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [columnFilters, setColumnFilters] = useState(DEFAULT_FILTERS);
  const [visibleColumnIds, setVisibleColumnIds] = useState(DEFAULT_VISIBLE_COLUMN_IDS);

  const { data: shopData } = useQuery(primaryShopIdQuery);
  const shopId = shopData?.primaryShopId;

  const {
    data,
    loading,
    refetch
  } = useQuery(accountsQuery, {
    variables: {
      shopIds: shopId ? [shopId] : [],
      first: 100,
      offset: 0,
      filters: {
        searchField: search || null
      }
    },
    fetchPolicy: "network-only",
    skip: !shopId
  });

  const [setAccountGroups, { loading: isSaving }] = useMutation(setAccountGroupsMutation);

  const accounts = data?.userAdministrationAccounts?.nodes || [];
  const totalCount = data?.userAdministrationAccounts?.totalCount || 0;

  useEffect(() => {
    let isCancelled = false;

    async function loadGroups() {
      if (!shopId) {
        setAllGroups([]);
        return;
      }

      setGroupsLoading(true);

      try {
        let after;
        let hasNextPage = true;
        const loadedGroups = [];

        while (hasNextPage && !isCancelled) {
          const { data: nextData } = await apolloClient.query({
            fetchPolicy: "network-only",
            query: groupsQuery,
            variables: {
              after,
              first: GROUPS_PAGE_SIZE,
              shopId
            }
          });

          const connection = nextData?.groups;
          const nextGroups = normalizeGroupCollection(connection);

          if (!nextGroups.length) {
            hasNextPage = false;
            break;
          }

          loadedGroups.push(...nextGroups);
          after = connection?.pageInfo?.endCursor;
          hasNextPage = Boolean(connection?.pageInfo?.hasNextPage && after);
        }

        if (!isCancelled) {
          setAllGroups(loadedGroups);
        }
      } catch (error) {
        if (!isCancelled) {
          setAllGroups([]);
          enqueueSnackbar("Could not load groups", { variant: "error" });
        }
      } finally {
        if (!isCancelled) {
          setGroupsLoading(false);
        }
      }
    }

    loadGroups();

    return () => {
      isCancelled = true;
    };
  }, [apolloClient, enqueueSnackbar, shopId]);

  const groups = useMemo(() => {
    const groupsFromQuery = allGroups;
    const groupsFromAccounts = accounts.flatMap((account) => accountGroups(account));
    const deduplicatedGroups = [...groupsFromQuery, ...groupsFromAccounts].reduce((result, group) => {
      if (group?._id) {
        result.set(group._id, group);
      }

      return result;
    }, new Map());

    return [...deduplicatedGroups.values()].sort((left, right) => String(left.name || left.slug || "").localeCompare(String(right.name || right.slug || ""), "de", {
      sensitivity: "base"
    }));
  }, [accounts, allGroups]);
  const availableGroups = useMemo(
    () => groups.filter((group) => !selectedGroups.some((selectedGroup) => selectedGroup._id === group._id)),
    [groups, selectedGroups]
  );
  const backendAccessGroups = useMemo(() => groups.filter(hasBackendAccessPermission), [groups]);
  const selectedBackendAccess = useMemo(() => hasBackendAccess(selectedGroups), [selectedGroups]);
  const selectedPermissions = useMemo(() => groupPermissions(selectedGroups), [selectedGroups]);
  const visiblePermissionGroups = useMemo(() => {
    const query = normalizeText(permissionSearch);

    return selectedGroups.map((group) => ({
      ...group,
      visiblePermissions: (group.permissions || []).filter((permission) => !query || normalizeText(permission).includes(query))
    })).filter((group) => group.visiblePermissions.length > 0 || !query);
  }, [permissionSearch, selectedGroups]);

  useEffect(() => {
    setSelectedGroups(accountGroups(selectedAccount));
    setPermissionSearch("");
  }, [selectedAccount]);

  const columns = useMemo(() => ([
    {
      id: "details",
      label: "Details",
      defaultVisible: true,
      filterType: null,
      hideable: false,
      sortable: false,
      render: (account) => (
        <Button size="small" variant="outlined" onClick={() => setSelectedAccount(account)}>
          Open
        </Button>
      )
    },
    {
      id: "name",
      label: "Name",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.name || "",
      sortable: true
    },
    {
      id: "firstName",
      label: "First Name",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => account.firstName || "",
      sortable: true
    },
    {
      id: "lastName",
      label: "Last Name",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => account.lastName || "",
      sortable: true
    },
    {
      id: "email",
      label: "Email",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.primaryEmailAddress || "",
      sortable: true
    },
    {
      id: "company",
      label: "Company",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.company || "",
      sortable: true
    },
    {
      id: "address",
      label: "Address",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.invoiceAddress?.address1 || "",
      sortable: true
    },
    {
      id: "city",
      label: "City",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.invoiceAddress?.city || "",
      sortable: true
    },
    {
      id: "postal",
      label: "Postal",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.invoiceAddress?.postal || "",
      sortable: true
    },
    {
      id: "country",
      label: "Country",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.invoiceAddress?.country || "",
      sortable: true
    },
    {
      id: "vatId",
      label: "VAT ID",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.vatId || "",
      sortable: true
    },
    {
      id: "datevId",
      label: "Datev ID",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => account.datevId || "",
      sortable: true
    },
    {
      id: "groups",
      label: "Groups",
      defaultVisible: true,
      filterType: "select",
      filterOptions: [
        { label: "All", value: "" },
        ...groups.map((group) => ({
          label: group.name || group.slug,
          value: group._id
        }))
      ],
      getFilterValue: (account) => accountGroups(account).map((group) => group._id),
      getSortValue: (account) => accountGroups(account).map((group) => group.name || group.slug).join(", "),
      render: (account) => (
        <Box className={classes.chips}>
          {accountGroups(account).slice(0, 3).map((group) => (
            <Chip key={group._id} size="small" label={group.name} />
          ))}
          {accountGroups(account).length > 3 && (
            <Chip size="small" label={`+${accountGroups(account).length - 3}`} />
          )}
        </Box>
      ),
      sortable: true
    },
    {
      id: "backendAccess",
      label: "Backend",
      defaultVisible: true,
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (account) => account.backendAccess,
      getSortValue: (account) => (account.backendAccess ? 1 : 0),
      render: (account) => (
        <>
          <span className={`${classes.backendDot} ${account.backendAccess ? classes.backendDotActive : classes.backendDotInactive}`} />
          {account.backendAccess ? "active" : "blocked"}
        </>
      ),
      sortable: true
    },
    {
      id: "confirmedEmail",
      label: "Verified",
      defaultVisible: true,
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (account) => account.confirmedEmail,
      getSortValue: (account) => (account.confirmedEmail ? 1 : 0),
      render: (account) => valueText(account.confirmedEmail),
      sortable: true
    },
    {
      id: "isActivated",
      label: "Activated",
      defaultVisible: true,
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (account) => account.isActivated,
      getSortValue: (account) => (account.isActivated ? 1 : 0),
      render: (account) => valueText(account.isActivated),
      sortable: true
    },
    {
      id: "invoicePaymentAllowed",
      label: "Invoice",
      defaultVisible: true,
      filterType: "boolean",
      filterOptions: BOOLEAN_FILTER_OPTIONS,
      getValue: (account) => account.invoicePaymentAllowed,
      getSortValue: (account) => (account.invoicePaymentAllowed ? 1 : 0),
      render: (account) => valueText(account.invoicePaymentAllowed),
      sortable: true
    },
    {
      id: "userType",
      label: "User Type",
      defaultVisible: true,
      filterType: "select",
      filterOptions: USER_TYPE_FILTER_OPTIONS,
      getValue: (account) => (account.isFamilyAndFriends ? "family" : "business"),
      render: (account) => (account.isFamilyAndFriends ? "Family & Friends" : "Business"),
      sortable: true
    },
    {
      id: "language",
      label: "Language",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => account.language || "",
      sortable: true
    },
    {
      id: "accountId",
      label: "Account ID",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => account._id || "",
      sortable: true
    },
    {
      id: "userId",
      label: "User ID",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => account.userId || "",
      sortable: true
    },
    {
      id: "specialRouteAccess",
      label: "Special Routes",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => (account.specialRouteAccess || []).join(", "),
      sortable: true
    },
    {
      id: "createdAt",
      label: "Registered",
      defaultVisible: true,
      filterType: "text",
      getValue: (account) => formatDate(account.createdAt),
      getSortValue: (account) => new Date(account.createdAt || 0).getTime(),
      sortable: true
    },
    {
      id: "updatedAt",
      label: "Updated",
      defaultVisible: false,
      filterType: "text",
      getValue: (account) => formatDate(account.updatedAt),
      getSortValue: (account) => new Date(account.updatedAt || 0).getTime(),
      sortable: true
    }
  ]), [classes.backendDot, classes.backendDotActive, classes.backendDotInactive, classes.chips, groups]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleColumnIds.includes(column.id)),
    [columns, visibleColumnIds]
  );

  const filteredAccounts = useMemo(
    () => accounts.filter((account) => columns.every((column) => matchesFilter(column, account, columnFilters[column.id]))),
    [accounts, columnFilters, columns]
  );

  const sortedAccounts = useMemo(() => {
    const activeColumn = columns.find((column) => column.id === sortBy);

    if (!activeColumn || activeColumn.sortable === false) {
      return filteredAccounts;
    }

    return [...filteredAccounts].sort((left, right) => {
      const leftValue = activeColumn.getSortValue ? activeColumn.getSortValue(left) : activeColumn.getValue(left);
      const rightValue = activeColumn.getSortValue ? activeColumn.getSortValue(right) : activeColumn.getValue(right);
      const result = compareSortValues(leftValue, rightValue);

      return sortDirection === "asc" ? result : result * -1;
    });
  }, [columns, filteredAccounts, sortBy, sortDirection]);

  const handleSave = async () => {
    if (!selectedAccount || !shopId) return;

    try {
      await setAccountGroups({
        variables: {
          input: {
            accountId: selectedAccount._id,
            groupIds: selectedGroups.map((group) => group._id),
            shopId
          }
        }
      });
      enqueueSnackbar("Groups saved", { variant: "success" });
      await refetch();
      setSelectedAccount({
        ...selectedAccount,
        backendAccess: hasBackendAccess(selectedGroups),
        groups: {
          ...selectedAccount.groups,
          nodes: selectedGroups
        }
      });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const handleCancel = () => {
    setSelectedGroups(accountGroups(selectedAccount));
  };

  const handleBackendAccessChange = (event) => {
    const shouldEnableBackendAccess = event.target.checked;

    setSelectedGroups((currentGroups) => {
      if (!shouldEnableBackendAccess) {
        return currentGroups.filter((group) => !hasBackendAccessPermission(group));
      }

      if (hasBackendAccess(currentGroups)) {
        return currentGroups;
      }

      const backendGroup = backendAccessGroups[0];
      if (!backendGroup) {
        return currentGroups;
      }

      return [...currentGroups, backendGroup];
    });
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection((currentDirection) => (currentDirection === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnId);
    setSortDirection("asc");
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((previousFilters) => ({
      ...previousFilters,
      [columnId]: value
    }));
  };

  const handleToggleColumn = (columnId) => {
    const column = columns.find((item) => item.id === columnId);

    if (!column || column.hideable === false) return;

    setVisibleColumnIds((previousIds) => (
      previousIds.includes(columnId)
        ? previousIds.filter((id) => id !== columnId)
        : [...previousIds, columnId]
    ));
  };

  const handleShowAllColumns = () => {
    setVisibleColumnIds(columns.map((column) => column.id));
  };

  const renderFilterInput = (column) => {
    const filterValue = columnFilters[column.id] || "";

    if (!column.filterType) {
      return null;
    }

    if (column.filterType === "select" || column.filterType === "boolean") {
      return (
        <TextField
          select
          fullWidth
          size="small"
          variant="outlined"
          className={classes.filterInput}
          value={filterValue}
          onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
        >
          {(column.filterOptions || []).map((option) => (
            <MenuItem key={`${column.id}-${option.value || "all"}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        className={classes.filterInput}
        value={filterValue}
        onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
      />
    );
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h5" gutterBottom>User Administration</Typography>

      <Paper>
        <Box className={classes.toolbar} p={2}>
          <TextField
            className={classes.search}
            label="Search name, email, company, account ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            variant="outlined"
            size="small"
          />
          <Box className={classes.actions}>
            <Typography className={classes.muted} variant="body2">
              {sortedAccounts.length} / {totalCount} users
            </Typography>
            <ColumnVisibilityMenu
              columns={columns}
              visibleColumnIds={visibleColumnIds}
              onToggleColumn={handleToggleColumn}
              onShowAll={handleShowAllColumns}
              title="Columns"
            />
            <IconButton onClick={() => refetch()} disabled={loading} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        <Box className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable === false ? (
                      column.label
                    ) : (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDirection : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell key={`${column.id}-filter`} className={classes.filterCell}>
                    {renderFilterInput(column)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center">No users found</TableCell>
                </TableRow>
              )}
              {!loading && sortedAccounts.map((account) => (
                <TableRow key={account._id} hover>
                  {visibleColumns.map((column) => (
                    <TableCell key={`${account._id}-${column.id}`}>
                      {column.render ? column.render(account) : valueText(column.getValue(account))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Drawer
        anchor="right"
        open={Boolean(selectedAccount)}
        onClose={() => setSelectedAccount(null)}
        PaperProps={{ className: classes.drawerPaper }}
      >
        {selectedAccount && (
          <Box className={classes.drawer}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{selectedAccount.name || selectedAccount.primaryEmailAddress}</Typography>
                <Typography className={classes.muted}>{selectedAccount.primaryEmailAddress}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedAccount(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box mt={3}>
              <Typography variant="subtitle1">Berechtigungen bearbeiten</Typography>
              <Typography className={classes.muted} variant="body2" gutterBottom>
                Rechte werden über Gruppen vergeben. Änderungen gelten nach dem Speichern.
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                openOnFocus
                options={groups}
                value={selectedGroups}
                getOptionLabel={(option) => option.name || option.slug}
                getOptionSelected={(option, value) => option._id === value._id}
                loading={groupsLoading}
                noOptionsText={shopId ? "Keine weiteren Gruppen gefunden" : "Shop wird geladen"}
                onChange={(event, value) => setSelectedGroups(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Zugeordnete Gruppen"
                    helperText={groups.length ? `${availableGroups.length} von ${groups.length} Gruppen verfügbar` : "Keine Gruppen verfügbar"}
                  />
                )}
              />
              <Box mt={2} display="flex" style={{ gap: 8 }}>
                <Button color="primary" variant="contained" disabled={isSaving} onClick={handleSave}>
                  Save
                </Button>
                <Button variant="outlined" disabled={isSaving} onClick={handleCancel}>
                  Cancel
                </Button>
              </Box>
            </Box>

            <Box mt={3}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1">Backend-Zugang</Typography>
                  <Typography className={classes.muted} variant="body2">
                    {selectedBackendAccess ? "Über eine der ausgewählten Gruppen aktiv" : "Kein Backend-Zugang vergeben"}
                  </Typography>
                </Box>
                <Checkbox
                  color="primary"
                  checked={selectedBackendAccess}
                  disabled={!backendAccessGroups.length}
                  onChange={handleBackendAccessChange}
                  inputProps={{ "aria-label": "Backend-Zugang" }}
                />
              </Box>
              {!backendAccessGroups.length && (
                <Typography className={classes.muted} variant="body2">
                  No group grants backend access
                </Typography>
              )}
            </Box>

            <Box mt={3} mb={2}>
              <Typography variant="subtitle1">Wirksame Berechtigungen ({selectedPermissions.length})</Typography>
              <Typography className={classes.muted} variant="body2" gutterBottom>
                Nach Gruppe aufgeschlüsselt, damit Herkunft und Umfang jedes Rechts klar bleiben.
              </Typography>
              {selectedPermissions.length ? (
                <>
                  <TextField
                    className={classes.permissionSearch}
                    fullWidth
                    label="Berechtigungen filtern"
                    onChange={(event) => setPermissionSearch(event.target.value)}
                    size="small"
                    value={permissionSearch}
                    variant="outlined"
                  />
                  <Box className={classes.permissionPanel}>
                    {visiblePermissionGroups.map((group) => (
                      <ExpansionPanel key={group._id} defaultExpanded={selectedGroups.length === 1}>
                        <ExpansionPanelSummary expandIcon={<ChevronDownIcon />}>
                          <Box className={classes.groupSummary}>
                            <Typography>{group.name || group.slug}</Typography>
                            <Chip label={`${group.visiblePermissions.length} Rechte`} size="small" />
                          </Box>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails className={classes.permissionGroup}>
                          <Box className={classes.chips}>
                            {group.visiblePermissions.map((permission) => <Chip key={permission} size="small" label={permission} />)}
                          </Box>
                        </ExpansionPanelDetails>
                      </ExpansionPanel>
                    ))}
                    {!visiblePermissionGroups.length && <Typography className={classes.muted}>Keine passende Berechtigung gefunden</Typography>}
                  </Box>
                </>
              ) : <Typography className={classes.muted}>Keine Berechtigungen über die ausgewählten Gruppen</Typography>}
            </Box>

            <Divider />

            <Box mt={2}>
              <Typography variant="subtitle1">Details</Typography>
              <Table size="small" className={classes.detailsTable}>
                <TableBody>
                  <DetailRow label="Account ID" value={selectedAccount._id} />
                  <DetailRow label="User ID" value={selectedAccount.userId} />
                  <DetailRow label="Name" value={selectedAccount.name} />
                  <DetailRow label="First name" value={selectedAccount.firstName} />
                  <DetailRow label="Last name" value={selectedAccount.lastName} />
                  <DetailRow label="Email" value={selectedAccount.primaryEmailAddress} />
                  <DetailRow label="Email verified" value={selectedAccount.confirmedEmail} />
                  <DetailRow label="Activated" value={selectedAccount.isActivated} />
                  <DetailRow label="Backend access" value={selectedAccount.backendAccess} />
                  <DetailRow label="Company" value={selectedAccount.company} />
                  <DetailRow label="VAT ID" value={selectedAccount.vatId} />
                  <DetailRow label="Datev ID" value={selectedAccount.datevId} />
                  <DetailRow label="Language" value={selectedAccount.language} />
                  <DetailRow label="Registered" value={formatDate(selectedAccount.createdAt)} />
                  <DetailRow label="Updated" value={formatDate(selectedAccount.updatedAt)} />
                  <DetailRow label="Special routes" value={selectedAccount.specialRouteAccess} />
                  <DetailRow label="Invoice address" value={[
                    selectedAccount.invoiceAddress?.fullName,
                    selectedAccount.invoiceAddress?.address1,
                    selectedAccount.invoiceAddress?.postal,
                    selectedAccount.invoiceAddress?.city,
                    selectedAccount.invoiceAddress?.country
                  ].filter(Boolean).join(", ")} />
                  <DetailRow label="Email records" value={(selectedAccount.emailRecords || []).map((email) => `${email.address} (${email.verified ? "verified" : "not verified"})`)} />
                  <DetailRow label="Address book entries" value={(selectedAccount.addressBook?.nodes || []).map((address) => [
                    address.fullName,
                    address.address1,
                    address.postal,
                    address.city,
                    address.country
                  ].filter(Boolean).join(", "))} />
                  <DetailRow label="Note" value={selectedAccount.note} />
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
